/**
 * Certificate Table Component
 * 
 * Displays a list of certificates with sorting, filtering, and actions
 * 
 * @component
 * @example
 * <CertificateTable
 *   certificates={certificates}
 *   onEdit={handleEdit}
 *   onDelete={handleDelete}
 *   onView={handleView}
 * />
 */

import { FC } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { FiEdit2, FiTrash2, FiEye } from 'react-icons/fi';
import Button from '@/components/Common/Button';
import { certificateColumns } from './certificateColumns';
import type { CertificateTableProps } from './CertificateTable.types';
import styles from './CertificateTable.module.css';

/**
 * CertificateTable Component
 */
const CertificateTable: FC<CertificateTableProps> = ({
  certificates,
  loading = false,
  onEdit,
  onDelete,
  onView,
}) => {
  // Table instance
  const table = useReactTable({
    data: certificates,
    columns: certificateColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: true,
  });

  // Loading state
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner} />
        <p>Carregando certificados...</p>
      </div>
    );
  }

  // Empty state
  if (!loading && certificates.length === 0) {
    return (
      <div className={styles.emptyContainer}>
        <p className={styles.emptyMessage}>
          📋 Nenhum certificado encontrado. Crie um novo certificado para começar.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead className={styles.thead}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={`${styles.th} ${
                      header.column.getCanSort() ? styles.sortable : ''
                    }`}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className={styles.headerContent}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <span className={styles.sortIcon}>
                          {header.column.getIsSorted() === 'asc'
                            ? ' ↑'
                            : header.column.getIsSorted() === 'desc'
                            ? ' ↓'
                            : ' ⇅'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
                <th className={styles.th} style={{ width: '120px' }}>
                  Ações
                </th>
              </tr>
            ))}
          </thead>
          <tbody className={styles.tbody}>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className={styles.tr}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={styles.td}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
                <td className={styles.td}>
                  <div className={styles.actionsCell}>
                    {onView && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconLeft={<FiEye size={16} />}
                        onClick={() => onView(row.original)}
                        title="Visualizar"
                      />
                    )}
                    {onEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconLeft={<FiEdit2 size={16} />}
                        onClick={() => onEdit(row.original)}
                        title="Editar"
                      />
                    )}
                    {onDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconLeft={<FiTrash2 size={16} />}
                        onClick={() => onDelete(row.original)}
                        title="Deletar"
                      />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CertificateTable;
